import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getUsersArr } from '../Store/Reducers';
import { currentUserHasPermission } from '../Utils/Utilities';
import { getAuthData } from '../Utils/AuthUtils';

/**
 * Custom hook to filter users based on connection permissions
 * @param {string|number} selectedConnID - The selected connection ID
 * @param {Array} connectionData - Array of connection objects
 * @returns {Array} - Filtered array of users who have access to the selected connection
 */
export const useUserConnectionFilter = (selectedConnID, connectionData = []) => {
    const users = useSelector(getUsersArr);

    const filteredUsers = useMemo(() => {
        if (!selectedConnID || !connectionData.length) {
            return users; // Return all users if no connection selected
        }

        // Convert selectedConnID to number for comparison
        const connID = Number(selectedConnID);
        if (!connID) {
            return users; // Return all users if invalid connection ID
        }

        // Find the selected connection
        const selectedConnection = connectionData.find(conn =>
            Number(conn.ConnID) === connID || Number(conn.connID) === connID
        );

        if (!selectedConnection) {
            return users; // Return all users if connection not found
        }

        const connType = selectedConnection.ConnType || selectedConnection.connTypes || '';
        const connectionPOCUserID = Number(selectedConnection.POCUserID || selectedConnection.pocUserID);

        // Filter users based on their permissions
        return users.filter(user => {
            // Try different ways to get user's ConnViewTypeID permission
            const userConnViewTypeID = user.ConnViewTypeID ||
                user.permissions?.ConnViewTypeID ||
                user.Permissions?.ConnViewTypeID ||
                user.connViewTypeID ||
                1; // Default to 1 (full access) if not found

            // ConnViewTypeID 1: Full access - show all users
            if (userConnViewTypeID === 1) {
                return true;
            }

            // ConnViewTypeID 2: Customer connections only
            if (userConnViewTypeID === 2) {
                return connType.includes('C') || connType.includes('P');
            }

            // ConnViewTypeID 3: Supplier connections only  
            if (userConnViewTypeID === 3) {
                return connType.includes('S');
            }

            // ConnViewTypeID 4: Only POC of the connection
            if (userConnViewTypeID === 4) {
                const pocUserIDs = selectedConnection.POCUserIDs ? selectedConnection.POCUserIDs.split(',') : [];
                const thrPocIDs = selectedConnection.ThrPOCUserIDs ? selectedConnection.ThrPOCUserIDs.split(',') : [];

                const isMainPOC = connectionPOCUserID === Number(user.userID);
                const isInPOCUserIDs = pocUserIDs.some(id => Number(id.trim()) === Number(user.userID));
                const isInThreadPOC = thrPocIDs.some(id => Number(id.trim()) === Number(user.userID));

                return isMainPOC || isInPOCUserIDs || isInThreadPOC;
            }

            // Default: show user
            return true;
        });
    }, [selectedConnID, connectionData]); // Fixed: Added proper dependencies

    return filteredUsers;
};